import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { publicApi } from '../services/api';

// A simple type that matches our backend response
interface PublicScheduleData {
  month: number;
  year: number;
  days: {
    date: string;
    shift: {
      id: number;
      date: string;
      am_employee_1: string | null;
      am_employee_2: string | null;
      pm_employee_1: string | null;
      pm_employee_2: string | null;
    } | null;
  }[];
}

const PublicSchedule: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduleData, setScheduleData] = useState<PublicScheduleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to get the current date in Central Time
  const getCurrentCentralDate = (): Date => {
    const now = new Date();
    now.setHours(now.getHours() - 6); // Simple Central Time adjustment
    return now;
  };

  // Check if a date is today (in Central Time)
  const isCentralToday = (date: string): boolean => {
    const centralToday = getCurrentCentralDate();
    const shiftDate = new Date(date);
    
    return (
      shiftDate.getDate() === centralToday.getDate() &&
      shiftDate.getMonth() === centralToday.getMonth() &&
      shiftDate.getFullYear() === centralToday.getFullYear()
    );
  };

  // Sort days by date
  const sortDaysByDate = (days: any[]): any[] => {
    return [...days].sort((a, b) => {
      const dateA = a.date.split('T')[0];
      const dateB = b.date.split('T')[0];
      return dateA.localeCompare(dateB);
    });
  };

  // Create calendar grid data
  const createCalendarGrid = () => {
    if (!scheduleData || !scheduleData.days || scheduleData.days.length === 0) return [];
    
    // Sort days by date
    const sortedDays = sortDaysByDate([...scheduleData.days]);
    
    // Get the month/year we're viewing
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    
    // Format the first day of the month as string
    const firstOfMonthStr = `${year}-${month.toString().padStart(2, '0')}-01`;
    
    // Get day of week (0-6, where 0 is Sunday)
    const firstDayOfWeek = new Date(firstOfMonthStr).getDay();
    
    // Get days in month
    const daysInMonth = new Date(year, month, 0).getDate();
    
    // Calculate days from previous month to show
    const daysFromPrevMonth = firstDayOfWeek;
    
    // Calculate previous month and year
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const daysInPrevMonth = new Date(prevYear, prevMonth, 0).getDate();
    
    // Calculate next month and year
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    
    // Create the calendar grid days array
    const calendarDays = [];
    
    // Add days from previous month
    for (let i = 0; i < daysFromPrevMonth; i++) {
      const prevMonthDay = daysInPrevMonth - daysFromPrevMonth + i + 1;
      const dateStr = `${prevYear}-${prevMonth.toString().padStart(2, '0')}-${prevMonthDay.toString().padStart(2, '0')}`;
      
      // Find the day in shifts data or create an empty one
      const day = scheduleData.days.find(d => d.date.startsWith(dateStr)) || { date: dateStr, shift: null };
      calendarDays.push({ ...day, currentMonth: false });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${year}-${month.toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      
      // Find the day in shifts data or create an empty one
      const day = scheduleData.days.find(d => d.date.startsWith(dateStr)) || { date: dateStr, shift: null };
      calendarDays.push({ ...day, currentMonth: true });
    }
    
    // Calculate remaining cells needed for a complete grid
    const remainingCells = 42 - calendarDays.length;
    
    // Add days from next month
    for (let i = 1; i <= remainingCells; i++) {
      const dateStr = `${nextYear}-${nextMonth.toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      
      // Find the day in shifts data or create an empty one  
      const day = scheduleData.days.find(d => d.date.startsWith(dateStr)) || { date: dateStr, shift: null };
      calendarDays.push({ ...day, currentMonth: false });
    }
    
    return calendarDays;
  };

  // Fetch data
  useEffect(() => {
    const fetchPublicSchedule = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const month = currentDate.getMonth() + 1;
        const year = currentDate.getFullYear();
        
        const response = await publicApi.get<PublicScheduleData>(`/public/public-schedule?month=${month}&year=${year}`);
        console.log('Public schedule API response:', response.data);
        
        // Log all days with shifts for debugging
        response.data.days.forEach(day => {
          if (day.shift) {
            console.log(`Day ${day.date.split('-')[2]} has shift:`, day.shift);
          }
        });
        
        setScheduleData(response.data);
      } catch (error) {
        console.error('Error fetching public schedule:', error);
        setError('Unable to load schedule. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPublicSchedule();
  }, [currentDate]);

  // Navigation
  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Render the calendar view
  const renderCalendarView = () => {
    if (!scheduleData || !scheduleData.days || scheduleData.days.length === 0) {
      return <div className="text-center p-4">No schedule data available</div>;
    }
    
    const calendarDays = createCalendarGrid();
    
    // Check if we're in mobile view
    const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 580;
    
    return (
      <div className={`mt-4 ${isMobileView ? 'mobile-view' : 'desktop-view'}`}>
        <div className="grid grid-cols-7 gap-1">
          {/* Weekday headers */}
          {[isMobileView ? 'S' : 'Sun', isMobileView ? 'M' : 'Mon', isMobileView ? 'T' : 'Tue', isMobileView ? 'W' : 'Wed', isMobileView ? 'T' : 'Thu', isMobileView ? 'F' : 'Fri', isMobileView ? 'S' : 'Sat'].map(day => (
            <div key={day} className={`text-center font-semibold ${isMobileView ? 'py-0.5 bg-gray-100 text-xs' : 'p-2 bg-gray-100 rounded'}`}>
              {day}
            </div>
          ))}
          
          {/* Calendar days */}
          {calendarDays.map((day, index) => {
            // Get date parts from the string date (YYYY-MM-DD)
            const dateStr = day.date.split('T')[0];
            const [yearStr, monthStr, dayStr] = dateStr.split('-');
            const dayOfMonth = parseInt(dayStr, 10);
            
            const isToday = isCentralToday(day.date);
            
            // Check if employees are assigned
            const hasAM = day.shift && (day.shift.am_employee_1 || day.shift.am_employee_2);
            const hasPM = day.shift && (day.shift.pm_employee_1 || day.shift.pm_employee_2);
            
            // Get background color based on shift assignment
            let bgColorClass = !day.currentMonth ? 'text-gray-400 bg-gray-50' : 'bg-white';
            
            // Build mobile employee display
            const renderMobileEmployees = () => {
              if (!day.shift) return null;
              
              const shift = day.shift;
              
              return (
                <div className="mobile-employee-display">
                  {/* AM Section */}
                  {(shift.am_employee_1 || shift.am_employee_2) && (
                    <div className="mobile-shift-section am-section">
                      {shift.am_employee_1 && (
                        <div className="am-shift mobile-employee">
                          {shift.am_employee_1}
                        </div>
                      )}
                      {shift.am_employee_2 && (
                        <div className="am-shift mobile-employee">
                          {shift.am_employee_2}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* PM Section */}
                  {(shift.pm_employee_1 || shift.pm_employee_2) && (
                    <div className="mobile-shift-section pm-section">
                      {shift.pm_employee_1 && (
                        <div className="pm-shift mobile-employee">
                          {shift.pm_employee_1}
                        </div>
                      )}
                      {shift.pm_employee_2 && (
                        <div className="pm-shift mobile-employee">
                          {shift.pm_employee_2}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            };
            
            // Build desktop employee display
            const renderDesktopEmployees = () => {
              if (!day.shift) return null;
              
              const shift = day.shift;
              
              return (
                <div className="mt-1 flex flex-col space-y-1">
                  {/* Morning shift */}
                  <div className="text-sm text-gray-700 font-medium px-1 rounded">AM:</div>
                  <div className="flex flex-wrap gap-1">
                    {/* AM Slot 1 */}
                    <div className="flex-1">
                      {shift.am_employee_1 ? (
                        <span className="px-2 py-0.5 rounded-full text-sm block bg-green-100 text-green-800">
                          {shift.am_employee_1}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                    
                    {/* AM Slot 2 */}
                    <div className="flex-1">
                      {shift.am_employee_2 ? (
                        <span className="px-2 py-0.5 rounded-full text-sm block bg-green-100 text-green-800">
                          {shift.am_employee_2}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Afternoon shift */}
                  <div className="text-sm text-gray-700 font-medium px-1 rounded">PM:</div>
                  <div className="flex flex-wrap gap-1">
                    {/* PM Slot 1 */}
                    <div className="flex-1">
                      {shift.pm_employee_1 ? (
                        <span className="px-2 py-0.5 rounded-full text-sm block bg-blue-100 text-blue-800">
                          {shift.pm_employee_1}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                    
                    {/* PM Slot 2 */}
                    <div className="flex-1">
                      {shift.pm_employee_2 ? (
                        <span className="px-2 py-0.5 rounded-full text-sm block bg-blue-100 text-blue-800">
                          {shift.pm_employee_2}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            };
            
            return (
              <div 
                key={day.date} 
                className={`${isMobileView ? 'p-0.5 overflow-hidden transition-colors' : 'p-2 rounded min-h-[100px] transition-colors'}
                  border ${isToday ? 'border-blue-500 border-2' : 'border-gray-200'}
                  ${bgColorClass}
                `}
              >
                <div className="flex justify-between items-center">
                  <div className={`${isMobileView ? 'text-xs' : 'text-sm'} font-medium ${isToday ? 'text-blue-600' : ''}`}>
                    {dayOfMonth}
                  </div>
                </div>
                
                {day.currentMonth && day.shift && (
                  isMobileView ? renderMobileEmployees() : renderDesktopEmployees()
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Public Schedule</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePreviousMonth}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              &lt; Previous
            </button>
            <div className="font-semibold">
              {format(currentDate, 'MMMM yyyy')}
            </div>
            <button
              onClick={handleNextMonth}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              Next &gt;
            </button>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center p-4">Loading schedule...</div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        ) : (
          renderCalendarView()
        )}
        
        <div className="mt-6 text-sm text-gray-500">
          <p>This is a public view of the barn schedule. For full details or to make changes, please log in.</p>
        </div>
      </div>
    </div>
  );
};

export default PublicSchedule; 